import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Swords, Shield, Store, Gem, MapPin, Calendar } from 'lucide-react';
import { api, compact, timeAgo } from '../lib/api';
import { useAuth } from '../lib/store';
import { HeroPortrait, Avatar, SkeletonList, ShopCard, SectionHead } from '../components/ui';

const BADGES = {
  top_shopper: { i: '🛍️', l: 'Top Shopper' },
  social_hero: { i: '📢', l: 'Social Hero' },
  gladiator: { i: '⚔️', l: 'Gladiator' },
  recruiter: { i: '🤝', l: 'Recruiter' },
};

export default function Profile() {
  const { username } = useParams();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ['profile', username], queryFn: () => api.get(`/users/${username}`).then(r => r.data) });

  const challengeM = useMutation({
    mutationFn: () => api.post(`/fights/challenge/${username}`, { stake: 0 }),
    onSuccess: () => toast.success('Challenge sent! ⚔️'),
    onError: e => toast.error(e.message),
  });

  if (isLoading || !data) return <SkeletonList n={3} h="h-40" />;
  const { user: u, hero, guild, shops, badges } = data;
  const isMe = u.username === user?.username;

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="card overflow-hidden">
        <div className="h-24 grad-brand" />
        <div className="p-4 -mt-16">
          <div className="flex items-end gap-4">
            {hero ? <HeroPortrait hero={hero} size="lg" />
              : <Avatar src={u.avatar_url} name={u.username} size={90} square />}
            <div className="flex-1 pb-2 min-w-0">
              <h1 className="font-display text-xl font-extrabold truncate">{hero?.name || u.display_name || u.username}</h1>
              <p className="text-sm text-ink-500 font-semibold">@{u.username}</p>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {u.role === 'producer' && <span className="chip-mint">PRODUCER</span>}
                {u.role === 'admin' && <span className="chip bg-ink-900 text-white">ADMIN</span>}
                {guild && <Link to={`/guild/${encodeURIComponent(guild.handle)}`} className="chip-grape">[{guild.tag}]</Link>}
                {u.city_handle && <Link to={`/g/${encodeURIComponent(u.city_handle)}`} className="chip-brand">{u.city_handle}</Link>}
              </div>
            </div>
          </div>

          {u.bio && <p className="text-sm text-ink-600 mt-3">{u.bio}</p>}

          <div className="flex gap-3 text-[11px] text-ink-400 font-semibold mt-2">
            <span className="flex items-center gap-1"><Calendar size={10} /> Joined {timeAgo(u.created_at)}</span>
            <span className="flex items-center gap-1"><Gem size={10} /> {compact(u.points_total)} lifetime</span>
          </div>

          {badges?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {badges.map(b => (
                <span key={b} className="chip bg-amber-100 text-amber-700">
                  {BADGES[b]?.i} {BADGES[b]?.l}
                </span>))}
            </div>)}

          {hero && (
            <div className="grid grid-cols-4 gap-2 mt-4">
              <S label="LEVEL" v={hero.level} /><S label="POWER" v={hero.power} />
              <S label="ELO" v={hero.elo} /><S label="WINS" v={`${hero.wins}/${hero.losses}`} />
            </div>)}

          {!isMe && hero && (
            <button onClick={() => challengeM.mutate()} disabled={challengeM.isPending}
              className="btn-primary w-full mt-3"><Swords size={15} /> Challenge to a duel</button>)}
          {isMe && <Link to="/hero" className="btn-dark w-full mt-3">Manage my hero</Link>}
        </div>
      </div>

      {hero && (
        <div className="card p-4">
          <SectionHead title="Characteristics" sub={`${hero.class.icon} ${hero.class.name}`} />
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {['strength', 'agility', 'vitality', 'intellect', 'spirit', 'charisma',
              'luck', 'endurance', 'precision_', 'defense', 'speed', 'willpower'].map(k => (
              <div key={k} className="flex items-center justify-between text-xs">
                <span className="font-semibold text-ink-500 capitalize">{k.replace('_', '')}</span>
                <span className="font-black tabular-nums">{hero[k]}</span>
              </div>))}
          </div>
        </div>
      )}

      {shops?.length > 0 && (
        <div>
          <SectionHead title="Shops" />
          <div className="space-y-2">{shops.map(s => <ShopCard key={s.id} shop={s} />)}</div>
        </div>
      )}
    </div>
  );
}

function S({ label, v }) {
  return (
    <div className="bg-ink-50 rounded-xl p-2 text-center">
      <p className="font-black text-base">{v}</p>
      <p className="text-[9px] font-bold text-ink-400">{label}</p>
    </div>
  );
}